import React, { useState } from 'react';
import {
  ArrowDownToLine, CalendarX, Check, ChevronRight, Clock, Crown, HelpCircle, Send, TriangleAlert, Wallet, X,
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { resultCardView } from './resultCardView';
import { CardStrips } from './ResultCardStrips';
import CardActions from './ResultCardActions';

// Per-tone classes: card border, head background, icon tile, eyebrow / headline text.
export const CARD_TONES = {
  positive: { card: 'border-positive/40', head: 'bg-positive/5', icon: 'bg-positive/15 text-positive', text: 'text-positive' },
  warning: { card: 'border-warning/40', head: 'bg-warning/5', icon: 'bg-warning/15 text-warning', text: 'text-warning' },
  negative: { card: 'border-negative/40', head: 'bg-negative/5', icon: 'bg-negative/15 text-negative', text: 'text-negative' },
};

const VALUE_TONES = { positive: 'text-positive', warning: 'text-warning', negative: 'text-negative', ink: 'text-ink' };

const ICONS = {
  check: Check, warning: TriangleAlert, clock: Clock, send: Send, help: HelpCircle,
  period: CalendarX, deposits: ArrowDownToLine, balance: Wallet, vip: Crown,
};

function Headline({ headline, tone }) {
  return (
    <div className="flex shrink-0 flex-col items-end gap-1 text-right">
      {headline.pill
        ? <span className={cn('rounded-full border px-2.5 py-0.5 text-xs font-bold', headline.tone === 'positive' ? 'border-positive/40 bg-positive/15 text-positive' : 'border-warning/40 bg-warning/15 text-warning')}>{headline.pill}</span>
        : <span className="text-xs font-medium text-ink-dim">{headline.label}</span>}
      <span className={cn('font-display text-3xl font-black leading-tight md:text-4xl', VALUE_TONES[headline.tone] || CARD_TONES[tone].text)}>{headline.value}</span>
      {headline.sub && <span className="text-xs font-medium text-ink-dim">{headline.sub}</span>}
      {headline.rate && <span className="font-mono text-xs font-medium text-ink-mute">{headline.rate}</span>}
    </div>
  );
}

function Head({ view }) {
  const tone = CARD_TONES[view.tone];
  const Icon = ICONS[view.icon] || HelpCircle;
  return (
    <div className={cn('flex flex-col gap-4 px-6 py-5', tone.head)}>
      <div className="flex flex-wrap items-start gap-4">
        <div className={cn('flex h-11 w-11 shrink-0 items-center justify-center rounded-lg', tone.icon)}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className={cn('text-xs font-bold uppercase tracking-wider', tone.text)}>{view.eyebrow}</div>
          <h2 className="font-display text-2xl font-black text-ink">{view.title}</h2>
          {view.text && <p className="text-sm font-medium text-ink-dim">{view.text}</p>}
          {view.reasons?.length > 0 && (
            <ul className={cn('mt-1 list-disc space-y-0.5 pl-4 text-sm font-medium', view.kind === 'unknown' ? 'text-ink-dim' : 'text-warning')}>
              {view.reasons.map((r) => <li key={r}>{r}</li>)}
            </ul>
          )}
        </div>
        {view.headline && <Headline headline={view.headline} tone={view.tone} />}
      </div>
      {view.progress && (
        <div className="flex flex-col gap-1.5">
          <div className="h-2 overflow-hidden rounded-full bg-surface-3">
            <div className="h-full rounded-full bg-negative" style={{ width: `${view.progress.pct}%` }} />
          </div>
          <span className="text-xs font-medium text-ink-dim">{view.progress.text}</span>
        </div>
      )}
    </div>
  );
}

function Stats({ stats }) {
  return (
    <div className="grid grid-cols-1 border-t border-white/10 sm:grid-cols-3">
      {stats.map((s) => (
        <div key={s.label} className="flex flex-col gap-1 border-b border-white/10 px-6 py-4 last:border-b-0 sm:border-b-0 sm:border-r sm:last:border-r-0">
          <span className="text-xs font-medium text-ink-dim">{s.label}</span>
          <span className={cn('font-display text-2xl font-black', s.muted ? 'text-ink-mute' : VALUE_TONES[s.tone] || 'text-ink')}>{s.value}</span>
          <span className="text-xs font-medium text-ink-mute">{s.sub}</span>
        </div>
      ))}
    </div>
  );
}

const DOT = {
  pass: { cls: 'bg-positive/15 text-positive', Icon: Check, label: 'text-ink' },
  warn: { cls: 'bg-warning/15 text-warning', Icon: TriangleAlert, label: 'text-warning' },
  fail: { cls: 'bg-negative text-white', Icon: X, label: 'font-bold text-negative' },
  unknown: { cls: 'bg-negative/15 text-negative', Icon: HelpCircle, label: 'font-bold text-negative' },
  skipped: { cls: 'border border-dashed border-white/20', Icon: null, label: 'text-ink-mute' },
};

// CheckDots: the four checks in order, with the link to the full calculation (simple view).
function CheckDots({ chips, linkText, onLink }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-dashed border-white/10 px-6 py-3">
      {chips.map((c) => {
        const dot = DOT[c.status] || DOT.skipped;
        return (
          <span key={c.label} className={cn('flex items-center gap-1.5 text-sm font-medium', dot.label)}>
            <span className={cn('flex h-4 w-4 items-center justify-center rounded-full', dot.cls)}>
              {dot.Icon && <dot.Icon className="h-3 w-3" />}
            </span>
            {c.label}
          </span>
        );
      })}
      <span className="flex-1" />
      {onLink && (
        <button type="button" onClick={onLink} className="flex items-center gap-1 text-sm font-bold text-brand-blue hover:underline">
          {linkText} <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

// StepTiles: the checks as tiles with the number behind each (detailed view).
function StepTiles({ steps }) {
  return (
    <div className="grid grid-cols-1 gap-3 border-t border-white/10 px-6 py-4 sm:grid-cols-2 lg:grid-cols-5">
      {steps.map((s) => {
        const dot = DOT[s.status] || DOT.skipped;
        return (
          <div key={s.label} className="flex items-start gap-2.5 rounded-lg border border-white/10 bg-surface-2/50 px-3 py-2.5">
            <span className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full', dot.cls)}>
              {dot.Icon && <dot.Icon className="h-3 w-3" />}
            </span>
            <div className="flex min-w-0 flex-col">
              <span className={cn('text-sm font-bold', s.status === 'skipped' ? 'text-ink-mute' : 'text-ink')}>{s.index} · {s.label}</span>
              <span className="truncate text-xs font-medium text-ink-dim">{s.sub}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ResultCard is the outcome of a comp request in one shape for every case: verdict and headline,
// three stats, the editable middle (wagering, amount, exception, comment), the checks, and one
// action row with the main button bottom-right.
export default function ResultCard({
  check, detailed, exception, setException, overrides, setOverrides,
  onCopy, onIssue, issuing, onCancel, cancelling, onShowDetails, onRecheck, backofficeUrl,
}) {
  // form: the override form (issuing although a check failed), null while closed
  const [ui, setUI] = useState({ comment: null, commentOpen: false, adjusting: false, guidance: false, form: null, confirmCancel: false });
  const view = resultCardView(check, { exception, overrides });
  const patchUI = (patch) => setUI((u) => ({ ...u, ...patch }));
  const strips = { check, view, detailed, ui, patchUI, exception, setException, overrides, setOverrides };
  return (
    <section aria-label="Result" className={cn('overflow-hidden rounded-xl border bg-surface-1', CARD_TONES[view.tone].card)}>
      <Head view={view} />
      <Stats stats={view.stats} />
      <CardStrips {...strips} />
      {detailed
        ? <StepTiles steps={view.steps} />
        : <CheckDots chips={view.chips} onLink={onShowDetails} linkText={view.kind === 'not_eligible' || view.kind === 'unknown' ? 'See all details' : 'See how it was calculated'} />}
      <CardActions
        {...strips}
        onCopy={onCopy} onIssue={onIssue} issuing={issuing}
        onCancel={onCancel} cancelling={cancelling} onRecheck={onRecheck} backofficeUrl={backofficeUrl}
      />
    </section>
  );
}
